import {
  Suspense, useState, useMemo, useRef, useEffect, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import PaymentCheckout from 'components/PaymentCheckout';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';
import usePaymentMethods, { isMethodSelectable } from 'components/PaymentMethods/hooks/usePaymentMethods';
import { activatePaymentMethod } from 'components/PaymentMethods/activation';
import { LazyBBVA, LazyMercadoPago, LazyPaymentReference } from 'components/PaymentMethods/lazyMethods';
import PaymentBox from 'components/PaymentBox';
import Button from 'components/UI/Button';
import { formatCurrency } from 'utils/formatCurrency';
import PaymentOutOfHour from 'components/PaymentOutOfHour';
import useOutOfOffice from 'utils/hooks/useOutOfOffice';
import {
  CONTRACT,
  CONTRACT_STATUS,
  PROVIDER_TYPE,
  SECTION,
  SECTION_MIN_AMOUNT,
  SECTION_PAYMENT_OPTIONS_KEYS,
  PAYMENT_OPTIONS_KEYS,
} from 'constants/global';
import { ROUTE_PATH } from 'routes';
import { contractsActions } from 'modules/contracts';
import { sectionsActions } from 'modules/sections';
import { useContractOrSection } from 'utils/hooks/useContractOrSection';
import { formatDate } from 'utils/formatDate';
import InputAmountSteps from 'components/UI/InputAmountSteps';
import Translate from 'components/Translate';
import Modal from 'components/Modal';
import InlineError from 'components/UI/InlineError';
import { translate } from 'utils/translate';
import { formatContractPaymentOptions } from 'utils/formatContratPaymentOption';
import isPaymentCardBlocked from 'utils/isPaymentCardBlocked';
import PaymentSummaryBarcode from 'components/PaymentSummaryBarcode';
import { getContractEmpaqueFieldsFromResponse } from 'utils/paymentSummaryEmpaque';
import { resolvePaymentSummaryBarcodeReference } from 'utils/paymentBarcodeReference';
import PaymentResumeEmpaqueRow from './components/PaymentResumeEmpaqueRow';
import ContractPaymentOptions from './components/ContractPaymentOptions';
import PaymentTypeAlertModal from './components/PaymentTypeAlertModal';
import './styles.scss';

const CARD_CLASSES = 'rounded-2xl bg-white p-4 shadow-card lg:p-6';

const ContractPay = () => {
  const literals = useLiterals('contractPay');
  const contractLiterals = useLiterals('contract');
  const checkoutLiterals = useLiterals('paymentCheckout');
  const dispatch = useDispatch();
  const { schedule: paymentsSchedule, isOutOfHour } = useOutOfOffice();
  const params = useParams();
  const navigate = useNavigate();
  const { type, contract, section } = useContractOrSection(ROUTE_PATH.CONTRACT_PAYMENT, params.id);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [coverageId, setCoverageId] = useState(null);
  const [amount, setAmount] = useState({ capital: 0, coverage: 0 });
  const [amountError, setAmountError] = useState({ capital: false, coverage: false, extra: false });
  const [extraAmount, setExtraAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showExtensionNotice, setShowExtensionNotice] = useState(false);
  const [showLiquidationAlert, setShowLiquidationAlert] = useState(false);
  const [step, setStep] = useState(0);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [activation, setActivation] = useState({ status: 'idle' });
  const latestProviderRef = useRef(null);
  const paymentEntity = contract || section;
  const cardPaymentBlocked = isPaymentCardBlocked(paymentEntity);
  const methods = usePaymentMethods({
    cardBlocked: cardPaymentBlocked,
    amount: (amount.capital || 0) + (amount.coverage || 0) + (extraAmount || 0),
  });

  // Clears a selection that is no longer valid (card block, PayCash over the limit).
  useEffect(() => {
    if (paymentMethod && !isMethodSelectable(methods, paymentMethod)) {
      setPaymentMethod(null);
    }
  }, [methods, paymentMethod]);

  const runActivation = useCallback((provider) => {
    latestProviderRef.current = provider;
    setActivation({ status: 'loading' });
    activatePaymentMethod(provider)
      .then(() => {
        if (latestProviderRef.current === provider) setActivation({ status: 'ready' });
      })
      .catch(() => {
        if (latestProviderRef.current === provider) setActivation({ status: 'error' });
      });
  }, []);

  // A cleared selection resets the activation state and drops pending results.
  useEffect(() => {
    if (!paymentMethod) {
      latestProviderRef.current = null;
      setActivation({ status: 'idle' });
    }
  }, [paymentMethod]);

  // Entering step 1 with a selection that was never activated re-runs activation.
  useEffect(() => {
    if (step === 1 && paymentMethod && latestProviderRef.current !== paymentMethod) {
      runActivation(paymentMethod);
    }
  }, [step, paymentMethod, runActivation]);

  const handleSelectMethod = (provider) => {
    setPaymentMethod(provider);
    runActivation(provider);
  };

  // The first time the user reaches "Forma de pago", PayCash (the recommended method)
  // comes preselected when it is available; any later choice is kept as is.
  const didPreselectRef = useRef(false);
  useEffect(() => {
    if (step !== 1 || didPreselectRef.current) return;
    didPreselectRef.current = true;
    if (!paymentMethod && isMethodSelectable(methods, PROVIDER_TYPE.PAYCASH)) {
      handleSelectMethod(PROVIDER_TYPE.PAYCASH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const isExtensionPayment = (payment) => {
    if (!payment || type !== CONTRACT) return false;
    return payment.paymentKey === String(PAYMENT_OPTIONS_KEYS.EXTENSION)
      || payment.paymentKey === String(PAYMENT_OPTIONS_KEYS.EXTENSION_EXT);
  };

  const payments = useMemo(() => {
    let options = [];
    let coverage = null;
    if (type === CONTRACT && contract) {
      options = formatContractPaymentOptions(contract?.OpcionesPago, literals);
    }
    if (type === SECTION && section) {
      if (section?.extension) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.EXTENSION,
          paymentKey: 'extensionPayment',
          title: literals.extensionPayment,
          amount: section.extensionPayment || 0,
          min: null,
          max: null,
          limitDate: section.expirationDate,
          lastTerm: section.currentTerm === section.maximumTerm,
        });
      }
      if (section?.minimum) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.MINIMUM,
          paymentKey: 'minimumPayment',
          title: literals.minimumPayment,
          amount: section.minimumPayment || 0,
          min: section.minimumPaymentWithDepositMin || null,
          max: section.minimumPaymentWithDepositMax || null,
          limitDate: section.expirationDate,
        });
      }
      // if (section?.suggested) {
      //   options.push({
      //     key: SECTION_PAYMENT_OPTIONS_KEYS.SUGGESTED,
      //     paymentKey: 'suggestedPayment',
      //     title: literals.suggestedPayment,
      //     amount: section.suggestedPayment || 0,
      //     min: section.suggestedPaymentWithDepositMin || null,
      //     max: section.suggestedPaymentWithDepositMax || null,
      //     limitDate: section.expirationDate,
      //   });
      // }
      if (section.remainingCapital >= 120 && section.currentTerm < section.maximumTerm) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.ABONO_20,
          paymentKey: 'abono20OrMore',
          title: literals.abono20OrMore,
          amount: 20,
          min: 20,
          max: section.remainingCapital - 100,
          limitDate: section.expirationDate,
        });
      }
      if (section.coverageBalance > 0 && section.coverageId) {
        const minPayment = Math.ceil(section.coverageAmount * 0.1);
        coverage = {
          id: section.coverageId,
          min: section.minimumCoverage || minPayment,
          max: section.coverageBalance || 0,
          balance: section.coverageBalance || 0,
          limit: Math.floor((section.coverageAmount || 0) * 0.9 - (section.coveragePaid || 0)),
        };
        coverage.unavailable = coverage.min > coverage.limit;
      }
    }
    return {
      options,
      coverage,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, section, type]);

  const handleClickStep0 = () => {
    setStep(1);
  };

  const handleSelectPayment = (payment) => {
    const selected = payments.options.find((option) => option.key === payment);
    setSelectedPayment(selected);
    setAmount((prev) => ({
      ...prev,
      capital: selected?.amount || 0,
      coverage: prev.coverage || payments?.coverage?.min || 0,
    }));
    setExtraAmount(0);
    setAmountError((prev) => ({ ...prev, capital: false, extra: false }));
    if (type === SECTION) {
      setShowExtensionNotice(selected?.key === 'extension' && selected?.lastTerm);
    }
    // Mostrar alerta si el tipo de pago es LIQUIDACION
    const isLiquidation = selected?.paymentKey === PAYMENT_OPTIONS_KEYS.LIQUIDACION
      || selected?.paymentKey === PAYMENT_OPTIONS_KEYS.LIQUIDACION_A
      || (selected?.title && selected.title.toLowerCase().includes('liquidaci'));
    if (isLiquidation) {
      setShowLiquidationAlert(true);
    }
  };

  const isSectionAdjustableAmount = type === SECTION
    && selectedPayment
    && (
      selectedPayment.key === SECTION_PAYMENT_OPTIONS_KEYS.MINIMUM
      || selectedPayment.key === SECTION_PAYMENT_OPTIONS_KEYS.ABONO_20
    );

  const handleSetAmount = (newAmount, key = 'capital') => {
    if (key === 'coverage') {
      const newAmountError = newAmount < payments.coverage.min || newAmount > payments.coverage?.limit;
      setAmount((prev) => ({ ...prev, coverage: newAmount }));
      setAmountError((prev) => ({ ...prev, coverage: newAmountError }));
      return;
    }

    const newAmountError = (isSectionAdjustableAmount && (newAmount === null || newAmount === ''))
      || newAmount < selectedPayment?.amount
      || (selectedPayment?.min
        && newAmount < selectedPayment.min
        && newAmount !== selectedPayment?.amount)
      || (selectedPayment?.max && newAmount > selectedPayment.max);

    setAmount((prev) => ({ ...prev, capital: newAmount }));
    setAmountError((prev) => ({ ...prev, capital: newAmountError }));
  };

  const handleSetExtraAmount = (newAmount) => {
    if (!selectedPayment) return;
    const maxAbono = selectedPayment?.max
      ? selectedPayment.max - (selectedPayment.amount || 0)
      : 0;
    const newAmountError = !!maxAbono
      && (newAmount < SECTION_MIN_AMOUNT || newAmount > maxAbono);
    setExtraAmount(newAmount);
    setAmountError((prev) => ({ ...prev, extra: newAmountError }));
  };

  const renderPaymentInfo = () => {
    const extensionSelected = isExtensionPayment(selectedPayment);
    const extensionAbonoMax = extensionSelected && selectedPayment?.max
      ? selectedPayment.max - (selectedPayment.amount || 0)
      : 0;
    return (
      <div>
        {payments.options.length > 0 ? (
          <ContractPaymentOptions
            options={payments.options}
            literals={literals}
            coverage={payments.coverage}
            selectedPayment={selectedPayment?.key || ''}
            selectedCoverage={coverageId}
            onSelectPayment={handleSelectPayment}
            onChangeCoverage={setCoverageId}
          />
        ) : (
          <InlineError className='mt-6 mb-6' error={translate(contractLiterals.noPaymentOptions)} />
        )}
        {selectedPayment && (
          <>
            <p className='mt-2 mb-2 text-dark-gray font-bold'>{literals.amount}</p>
            {!extensionSelected && (
              <InputAmountSteps
                value={amount.capital}
                originalValue={selectedPayment.amount}
                min={selectedPayment.min}
                max={selectedPayment.max}
                error={amountError.capital}
                clampOnBlur={!isSectionAdjustableAmount}
                onChange={(v) => handleSetAmount(v, 'capital')}
              />
            )}
            {extensionSelected && (
              <p className='mt-1 mb-2'>
                <b>{formatCurrency(selectedPayment.amount)}</b>
              </p>
            )}
            {extensionSelected && (
              <>
                <p className='mt-4 mb-2 font-bold'>{literals.abono20OrMore}</p>
                <InputAmountSteps
                  value={extraAmount}
                  originalValue={0}
                  min={SECTION_MIN_AMOUNT}
                  max={extensionAbonoMax}
                  error={amountError.extra}
                  onChange={(v) => handleSetExtraAmount(v)}
                />
              </>
            )}
            {coverageId ? (
              <>
                <p className='mt-4 mb-2 font-bold'>{literals.coverageAmount}</p>
                <InputAmountSteps
                  value={amount.coverage}
                  min={payments.coverage.min}
                  max={payments.coverage.limit}
                  error={amountError.coverage}
                  decimals={false}
                  onChange={(v) => handleSetAmount(v, 'coverage')}
                />
                {amount.coverage > payments.coverage.limit
                  && amount.coverage <= payments.coverage.max && (
                  <Translate
                    className='mt-2 mb-0 text-danger'
                    literal={literals.maxAmountCoverage}
                    vars={{ amount: formatCurrency(payments.coverage.limit) }}
                  />
                )}
                {!amountError.coverage && !amountError.capital && !amountError.extra && (
                  <Translate
                    className='mt-6 mb-0 font-bold'
                    literal={literals.totalAmountToPay}
                    vars={{
                      amount: formatCurrency(
                        amount.capital + amount.coverage + (extraAmount || 0),
                      ),
                    }}
                  />
                )}
              </>
            ) : null}
            <Button
              onClick={handleClickStep0}
              className='mt-6'
              disabled={
                !selectedPayment
                || amountError.capital
                || amountError.coverage
                || amountError.extra
              }
              text={literals.common.next}
            />
          </>
        )}
      </div>
    );
  };

  const renderOutOfHour = () => {
    return (
      <>
        <PaymentBox
          type={type}
          data={contract || section || null}
          showAction={false}
          inBox={false}
        />
        <PaymentOutOfHour schedule={paymentsSchedule} />
      </>
    );
  };

  const renderStep0 = () => {
    return (
      <div className={CARD_CLASSES}>
        <PaymentBox
          type={type}
          data={contract || section || null}
          showAction={false}
          inBox={false}
        />
        {renderPaymentInfo()}
      </div>
    );
  };

  const renderStep1 = () => (
    <PaymentMethodSelector
      methods={methods}
      selected={paymentMethod}
      onSelect={handleSelectMethod}
      activation={activation}
      onRetry={() => paymentMethod && runActivation(paymentMethod)}
      onPay={() => setStep(2)}
    />
  );

  const renderStep2 = () => {
    const capitalWithAbono = amount.capital + (extraAmount || 0);
    if (paymentMethod === PROVIDER_TYPE.PAYCASH || paymentMethod === PROVIDER_TYPE.PUNTORED) {
      return (
        <LazyPaymentReference
          type={type}
          provider={paymentMethod}
          id={params.id}
          payment={selectedPayment}
          amount={capitalWithAbono}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.BANCOMER) {
      return (
        <LazyBBVA
          type={type}
          id={params.id}
          payment={selectedPayment}
          amount={capitalWithAbono}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
          onClose={() => {
            setStep(1);
          }}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.MERCADO_PAGO) {
      const totalAmount = capitalWithAbono + (coverageId ? amount.coverage : 0);
      return (
        <LazyMercadoPago
          amount={capitalWithAbono}
          type={type}
          id={params.id}
          payment={selectedPayment}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
          onClose={() => {
            // Volver a la selección de método de pago (step 1), no quedarse en
            // el paso "Realizar pago" mostrando "Próximamente" (WEB_MLS-532).
            setStep(1);
            setPaymentMethod(null);
            const path = type === CONTRACT
              ? ROUTE_PATH.setContractPayment(params.id)
              : ROUTE_PATH.setSectionPayment(params.id);
            navigate(path, { replace: true });
          }}
          onSuccess={(response) => {
            if (type === CONTRACT) {
              dispatch(contractsActions.needUpdate());
            } else {
              dispatch(sectionsActions.needUpdate());
            }
            const responseData = response?.data ?? response;
            const barcodeReference = resolvePaymentSummaryBarcodeReference({
              payment: responseData,
              type,
              id: params.id,
              section,
              date: formatDate(new Date(), 'DD/MM/YYYY'),
              context: type === SECTION ? 'apartado' : 'response',
            });
            setPaymentSummary({
              id: params.id,
              type,
              paymentName: selectedPayment?.title || selectedPayment?.paymentName,
              paymentKey: selectedPayment?.paymentKey,
              ...getContractEmpaqueFieldsFromResponse(response, params.id),
              amount: amount.capital + (extraAmount || 0),
              coverage: coverageId ? amount.coverage : 0,
              totalAmount,
              date: new Date(),
              status: CONTRACT_STATUS.COMPLETED,
              ...(barcodeReference != null ? { barcodeReference } : {}),
            });
          }}
          onPending={(pendingResult) => {
            if (type === CONTRACT) {
              dispatch(contractsActions.needUpdate());
            } else {
              dispatch(sectionsActions.needUpdate());
            }
            setPaymentSummary({
              id: params.id,
              type,
              paymentName: selectedPayment?.title || selectedPayment?.paymentName,
              paymentKey: selectedPayment?.paymentKey,
              ...getContractEmpaqueFieldsFromResponse(pendingResult, params.id),
              amount: amount.capital + (extraAmount || 0),
              coverage: coverageId ? amount.coverage : 0,
              totalAmount,
              date: new Date(),
              status: CONTRACT_STATUS.PENDING,
              warningMessage: pendingResult?.message,
              mpStatus: pendingResult?.status,
              mpStatusDetail: pendingResult?.statusDetail,
              mpPaymentId: pendingResult?.paymentId,
            });
          }}
        />
      );
    }
    return <h1>Próximamente</h1>;
  };

  const handleBack = () => {
    if (paymentSummary) {
      setPaymentSummary(null);
      setStep(0);
      setPaymentMethod(null);
      navigate(ROUTE_PATH.PAYMENTS);
      return;
    }
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    navigate(-1);
  };

  const renderPaymentSummary = () => {
    if (!paymentSummary) return null;
    const summary = paymentSummary;
    return (
      <div className='payment-resume'>
        <h1 className='mb-6 text-primary'>{literals.paymetResume}</h1>
        {summary.warningMessage && (
          <div className='alert alert-warning mb-4' role='alert'>
            <strong>{literals.warningTitle || 'Atención'}</strong>
            {' '}
            {summary.warningMessage}
          </div>
        )}
        <div className='payment-resume-contract-info mb-2'>
          <div className='flex justify-between'>
            <h2 className='font-bold'>{literals.common[summary.type.toLowerCase()]}</h2>
            <h3 className='text-neutral-500'>{summary.id}</h3>
          </div>
          <PaymentResumeEmpaqueRow payment={summary} label={literals.empaque} />
        </div>
        {summary.paymentName ? (
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{literals.paymentConcept}</h2>
            <h3 className='text-neutral-500 text-right'>{summary.paymentName}</h3>
          </div>
        ) : null}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.date}</h2>
          <h3 className='text-neutral-500'>{formatDate(summary.date, 'DD/MM/YYYY')}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.paymentMethod2}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.mercadopago}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.status}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.statusValues[summary.status] || summary.status}</h3>
        </div>
        {summary.status === CONTRACT_STATUS.COMPLETED && (
          <PaymentSummaryBarcode reference={summary.barcodeReference} />
        )}
        <div className='separator my-4' />
        {summary.coverage > 0 && (
          <>
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{summary.type === CONTRACT ? literals.contractAmount : literals.sectionAmount}</h2>
              <h3 className='text-primary font-bold'>{formatCurrency(summary.amount)}</h3>
            </div>
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{literals.coverageAmount}</h2>
              <h3 className='text-primary font-bold'>{formatCurrency(summary.coverage)}</h3>
            </div>
          </>
        )}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.totalAmount}</h2>
          <h3 className='text-primary font-bold'>{formatCurrency(summary.totalAmount)}</h3>
        </div>
        <Button
          className='mt-6'
          type='primary'
          text={literals.common.back}
          onClick={() => {
            setPaymentSummary(null);
            setStep(0);
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS);
          }}
        />
      </div>
    );
  };

  const checkoutSteps = useMemo(() => [
    { label: checkoutLiterals.steps.paymentOptions },
    { label: checkoutLiterals.steps.paymentMethod },
    { label: checkoutLiterals.steps.confirmation },
  ], [checkoutLiterals]);

  const entityNumber = (type === CONTRACT ? contract?.Numero : section?.id) || params.id;
  const selectedCoverage = coverageId ? (amount.coverage || 0) : 0;

  const checkoutSummary = useMemo(() => {
    if (!selectedPayment) return null;
    const capital = amount.capital || 0;
    const extra = extraAmount || 0;
    const extras = [];
    if (extra > 0) {
      extras.push({ id: 'extraAmount', label: checkoutLiterals.summary.extraAmount, amount: extra });
    }
    if (selectedCoverage > 0) {
      extras.push({ id: 'coverage', label: checkoutLiterals.summary.coverage, amount: selectedCoverage });
    }
    return {
      items: [{
        id: String(entityNumber),
        label: translate(
          type === CONTRACT ? checkoutLiterals.summary.contract : checkoutLiterals.summary.section,
          { number: entityNumber },
        ),
        description: selectedPayment?.title,
        amount: capital,
      }],
      extras,
      total: capital + extra + selectedCoverage,
    };
  }, [selectedPayment, amount.capital, extraAmount, selectedCoverage, entityNumber, type, checkoutLiterals]);

  const showSteps = !isOutOfHour && !paymentSummary;

  return (
    <PaymentCheckout
      title={showSteps ? checkoutSteps[step]?.label : literals.title}
      onBack={handleBack}
      steps={checkoutSteps}
      currentStep={step}
      showStepper={showSteps}
      summary={checkoutSummary}
    >
      <div className='contract-payment__container'>
        {isOutOfHour && <div className={CARD_CLASSES}>{renderOutOfHour()}</div>}
        {!isOutOfHour && paymentSummary && <div className={CARD_CLASSES}>{renderPaymentSummary()}</div>}
        {showSteps && step === 0 && renderStep0()}
        {showSteps && step === 1 && renderStep1()}
        {showSteps && step === 2 && (
          // Mercado Pago already renders its own card; wrapping it again doubles the frame.
          <div className={paymentMethod === PROVIDER_TYPE.MERCADO_PAGO ? '' : CARD_CLASSES}>
            <Suspense fallback={<p className='m-0 text-center text-neutral-500'>{checkoutLiterals.loadingMethod}</p>}>
              {renderStep2()}
            </Suspense>
          </div>
        )}
      </div>
      {showExtensionNotice && (
        <Modal
          onClose={() => setShowExtensionNotice(false)}
          showCloseButton={false}
          closeOnBackgroundClick={false}
        >
          <Translate className='mt-2 mb-4' literal={literals.sectionExtensionNotice} />
          <p className='text-right'>
            <b>{`${literals.pendingAmount}: ${formatCurrency(section?.remainingCapital)}`}</b>
          </p>
          <Button text={literals.common.close} onClick={() => setShowExtensionNotice(false)} />
        </Modal>
      )}
      <PaymentTypeAlertModal
        show={showLiquidationAlert}
        title={literals.liquidationAlertTitle}
        message={literals.liquidationAlertMessage}
        buttonText={literals.accept}
        onClose={() => setShowLiquidationAlert(false)}
      />
    </PaymentCheckout>
  );
};

export default ContractPay;
